import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TableUI } from "components/design/TableUI";
import { COA_IMPORT_REVIEW_COLUMNS } from "components/QBO/ReviewStep";
import { useToast } from "hooks/useToast";
import { getCoreRowModel, useReactTable } from "react-table-8.10.7";
import { useGetCOAImportStateQuery } from "store/apis/chartOfAccounts";
import { useFinalizeFinallyImportMutation } from "store/apis/finallyImport";
import { ImportCOAData } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { useImportFinally } from "./ImportFinallyContext";
import { ImportFinallyHeader } from "./ImportFinallyHeader";

type FinallyReviewStepProps = {
  importProcessId: string;
};

const EMPTY_COA_DATA: ImportCOAData[] = [];

export const FinallyReviewStep = ({
  importProcessId,
}: FinallyReviewStepProps) => {
  const { entityId, onNext, isOpen, close } = useImportFinally();
  const { alertToast, info: infoToast } = useToast();
  const [finalizeFinallyImport, { isLoading: isFinalizing }] =
    useFinalizeFinallyImportMutation();

  const { data, isLoading, isSuccess, isError } = useGetCOAImportStateQuery(
    { entityId, coaImportStateId: importProcessId },
    { skip: !entityId || !importProcessId, refetchOnMountOrArgChange: true }
  );

  const reviewCoaData = data?.coa_data ?? EMPTY_COA_DATA;
  const isEmpty = reviewCoaData.length === 0;

  const table = useReactTable({
    data: reviewCoaData,
    columns: COA_IMPORT_REVIEW_COLUMNS,
    getRowId: (row) => row.identifier.toString(),
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  const handleOpenChange = () => close({ takeConfirmation: true });

  const handleImport = async () => {
    try {
      await finalizeFinallyImport({ entityId }).unwrap();
      infoToast({
        title: "Importing",
        message: "We'll update you once done",
      });
      onNext();
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ??
            "Something went wrong",
        },
        error as Error
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <Modal.Content size="xxl" useCustomOverlay>
        <ImportFinallyHeader />
        <Modal.Body className="t-flex t-flex-col t-gap-4">
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isError={isError}
            isEmpty={isEmpty}
          >
            <Async.Empty>
              <div className="t-text-center t-h-60 t-text-body-sm t-text-text-100 t-flex t-items-center t-justify-center">
                No categories found in the imported Finally data.
              </div>
            </Async.Empty>
            <Async.ErrorHandler>
              <div className="t-text-center t-h-60 t-text-body-sm t-text-text-100 t-flex t-items-center t-justify-center">
                Something went wrong while loading the imported data.
              </div>
            </Async.ErrorHandler>
            <Async.Success>
              <TableUI table={table} />
            </Async.Success>
          </Async.Root>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button type="button" disabled={isFinalizing}>
              Cancel
            </Button>
          </Modal.RawClose>
          <Button
            type="button"
            customType="primary"
            onClick={handleImport}
            isLoading={isFinalizing}
            disabled={isEmpty || isFinalizing}
          >
            Import
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
